/**
 * Buydown calculation utilities for builder incentive scenarios
 */

export interface BuydownConfiguration {
  // Points costs to builder
  fixedPoints: number;
  oneZeroPoints: number;
  twoOnePoints: number;
  threeTwoOnePoints: number;
  
  // Custom interest rates for each scenario
  fixedRate: number;
  
  // 1/0 rates
  oneZeroYear1Rate: number;
  oneZeroYear2PlusRate: number;
  
  // 2/1 rates
  twoOneYear1Rate: number;
  twoOneYear2Rate: number;
  twoOneYear3PlusRate: number;
  
  // 3/2/1 rates
  threeTwoOneYear1Rate: number;
  threeTwoOneYear2Rate: number;
  threeTwoOneYear3Rate: number;
  threeTwoOneYear4PlusRate: number;
  
  // Builder credit percentages
  fixedBuilderCreditPercent: number;
  oneZeroBuilderCreditPercent: number;
  twoOneBuilderCreditPercent: number;
  threeTwoOneBuilderCreditPercent: number;
  
  // Occupancy type and investor adjustments
  occupancyType?: 'primary' | 'investment' | 'second';
  investorAdditionalPoints?: number;
}

export interface BuydownScenario {
  type: 'fixed' | '1/0' | '2/1' | '3/2/1';
  name: string;
  description: string;
  points: number;
  pointsCost: number;
  builderCreditPercent: number;
  builderCredit: number;
  paymentSchedule: {
    period: string;
    rate: number;
    monthlyPayment: number;
    principalAndInterest: number;
    propertyTaxes: number;
    homeInsurance: number;
    pmi: number;
    hoa: number;
  }[];
  cashToCloseBeforeCredit: number;
  earnestMoneyDeposit: number;
  cashToClose: number;
  extraCashNeeded: number;
  breakEvenMonths?: number;
  totalSavings?: number;
}

export interface BuydownCalculationInputs {
  loanAmount: number;
  loanType: 'conventional' | 'fha' | 'va' | 'usda';
  baseRate: number;
  purchasePrice: number;
  downPayment: number;
  monthlyTaxes: number;
  monthlyInsurance: number;
  monthlyHoa: number;
  monthlyPmi: number;
  totalClosingCosts: number;
  earnestMoneyDeposit: number;
  configuration: BuydownConfiguration;
}

/**
 * Get default configuration based on loan type and base rate
 */
export const getDefaultConfiguration = (
  loanType: string,
  baseRate: number,
  occupancyType: 'primary' | 'investment' | 'second' = 'primary'
): BuydownConfiguration => {
  // Determine if conventional or FHA/VA/USDA based on loan type
  const isConventional = loanType === 'conventional';
  
  // Base rate points for 4.99% fixed (updated Mar 2026):
  // FHA/VA/USDA (Primary): 3.402 points
  // Conventional (Primary): 4.355 points
  // Conventional (Investor/2nd Home): 4.355 points + investor adjustment
  let basePoints = isConventional ? 4.355 : 3.402;
  
  // Add investor adjustment for conventional loans only
  // Example: With 25% down and 740 score, the investor cost is an additional 2.125 points
  const investorAdditionalPoints = 2.125;
  if (isConventional && (occupancyType === 'investment' || occupancyType === 'second')) {
    basePoints += investorAdditionalPoints;
  }
  
  // Buydown point costs (added to base rate cost)
  // Conventional: 1/0 = 0.75 points, 2/1 = 2.25 points
  // FHA/VA/USDA: 1/0 = 2 points, 2/1 = 3.5 points (only eligible for completed homes over 150 days old), 3/2/1 = 5.6 points
  const oneZeroAdditionalPoints = isConventional ? 0.75 : 2.0;
  const twoOneAdditionalPoints = isConventional ? 2.25 : 3.5;
  const threeTwoOneAdditionalPoints = 5.6; // Only available for FHA/VA/USDA
  
  return {
    // Points - base rate cost plus buydown adjusters (with investor adjustment already included in basePoints)
    fixedPoints: parseFloat(basePoints.toFixed(3)),
    oneZeroPoints: parseFloat((basePoints + oneZeroAdditionalPoints).toFixed(3)),
    twoOnePoints: parseFloat((basePoints + twoOneAdditionalPoints).toFixed(3)),
    // 3/2/1 only available for FHA/VA/USDA (only eligible for completed homes over 150 days old), 5.6 points adjuster; for conventional estimate based on 2/1
    threeTwoOnePoints: isConventional 
      ? parseFloat((basePoints + (twoOneAdditionalPoints * 1.5)).toFixed(3))
      : parseFloat((basePoints + threeTwoOneAdditionalPoints).toFixed(3)), // = 9.002 total points
    
    // Fixed rate - static 4.99%
    fixedRate: 4.99,
    
    // 1/0 rates - static values
    oneZeroYear1Rate: 3.99,
    oneZeroYear2PlusRate: 4.99,
    
    // 2/1 rates - static values
    twoOneYear1Rate: 2.99,
    twoOneYear2Rate: 3.99,
    twoOneYear3PlusRate: 4.99,
    
    // 3/2/1 rates - static values
    threeTwoOneYear1Rate: 1.99,
    threeTwoOneYear2Rate: 2.99,
    threeTwoOneYear3Rate: 3.99,
    threeTwoOneYear4PlusRate: 4.99,
    
    // Builder credits
    fixedBuilderCreditPercent: 2.0,
    oneZeroBuilderCreditPercent: 1.5,
    twoOneBuilderCreditPercent: 1.0,
    threeTwoOneBuilderCreditPercent: 0.0,
    
    // Occupancy type and investor adjustment
    occupancyType,
    investorAdditionalPoints: (isConventional && occupancyType !== 'primary') 
      ? investorAdditionalPoints 
      : 0
  };
};

/**
 * Calculate monthly P&I payment
 */
const calculateMonthlyPI = (principal: number, annualRate: number, years: number = 30): number => {
  const monthlyRate = annualRate / 100 / 12;
  const numberOfPayments = years * 12;
  
  if (monthlyRate === 0) {
    return principal / numberOfPayments;
  }
  
  return principal * 
    (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
    (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
};

/**
 * Calculate all buydown scenarios
 */
export const calculateBuydownScenarios = (
  inputs: BuydownCalculationInputs
): BuydownScenario[] => {
  const { 
    loanAmount, 
    purchasePrice,
    downPayment,
    monthlyTaxes,
    monthlyInsurance,
    monthlyHoa,
    monthlyPmi,
    totalClosingCosts,
    earnestMoneyDeposit,
    configuration
  } = inputs;

  const onePoint = loanAmount * 0.01; // 1 point = 1% of loan amount

  // Calculate base monthly payment (using configuration's fixed rate as base)
  // Include all PITI components plus HOA and PMI for accurate total
  const basePI = calculateMonthlyPI(loanAmount, configuration.fixedRate);
  const baseMonthlyPayment = basePI + monthlyTaxes + monthlyInsurance + monthlyHoa + monthlyPmi;

  const scenarios: BuydownScenario[] = [];

  // Calculate cash to close before any builder credit (base amount)
  const cashToCloseBeforeCredit = Math.max(0, downPayment + totalClosingCosts);

  // Fixed Rate Scenario
  const fixedBuilderCredit = purchasePrice * (configuration.fixedBuilderCreditPercent / 100);
  const fixedCashToClose = Math.max(0, cashToCloseBeforeCredit - fixedBuilderCredit - earnestMoneyDeposit);
  
  scenarios.push({
    type: 'fixed',
    name: `${configuration.fixedRate}% Fixed Rate`,
    description: 'Standard fixed rate with maximum builder incentive',
    points: configuration.fixedPoints,
    pointsCost: configuration.fixedPoints * onePoint,
    builderCreditPercent: configuration.fixedBuilderCreditPercent,
    builderCredit: fixedBuilderCredit,
    paymentSchedule: [
      {
        period: 'All 30 years',
        rate: configuration.fixedRate,
        monthlyPayment: baseMonthlyPayment,
        principalAndInterest: basePI,
        propertyTaxes: monthlyTaxes,
        homeInsurance: monthlyInsurance,
        pmi: monthlyPmi,
        hoa: monthlyHoa
      }
    ],
    cashToCloseBeforeCredit,
    earnestMoneyDeposit,
    cashToClose: fixedCashToClose,
    extraCashNeeded: 0
  });

  // 1/0 Buydown Scenario
  const year1PI_1_0 = calculateMonthlyPI(loanAmount, configuration.oneZeroYear1Rate);
  const year1Payment_1_0 = year1PI_1_0 + monthlyTaxes + monthlyInsurance + monthlyHoa + monthlyPmi;
  
  const year2PI_1_0 = calculateMonthlyPI(loanAmount, configuration.oneZeroYear2PlusRate);
  const year2Payment_1_0 = year2PI_1_0 + monthlyTaxes + monthlyInsurance + monthlyHoa + monthlyPmi;
  
  const builderCredit_1_0 = purchasePrice * (configuration.oneZeroBuilderCreditPercent / 100);
  const cashToClose_1_0 = Math.max(0, cashToCloseBeforeCredit - builderCredit_1_0 - earnestMoneyDeposit);
  const extraCash_1_0 = cashToClose_1_0 - fixedCashToClose;
  
  const monthlySavings_1_0 = baseMonthlyPayment - year1Payment_1_0;
  const totalSavings_1_0 = monthlySavings_1_0 * 12;
  const breakEven_1_0 = extraCash_1_0 > 0 ? Math.ceil(extraCash_1_0 / monthlySavings_1_0) : 0;

  scenarios.push({
    type: '1/0',
    name: '1/0 Buydown',
    description: 'First year rate reduced by 1%, then normal rate',
    points: configuration.oneZeroPoints,
    pointsCost: configuration.oneZeroPoints * onePoint,
    builderCreditPercent: configuration.oneZeroBuilderCreditPercent,
    builderCredit: builderCredit_1_0,
    paymentSchedule: [
      {
        period: 'Year 1',
        rate: configuration.oneZeroYear1Rate,
        monthlyPayment: year1Payment_1_0,
        principalAndInterest: year1PI_1_0,
        propertyTaxes: monthlyTaxes,
        homeInsurance: monthlyInsurance,
        pmi: monthlyPmi,
        hoa: monthlyHoa
      },
      {
        period: 'Years 2-30',
        rate: configuration.oneZeroYear2PlusRate,
        monthlyPayment: year2Payment_1_0,
        principalAndInterest: year2PI_1_0,
        propertyTaxes: monthlyTaxes,
        homeInsurance: monthlyInsurance,
        pmi: monthlyPmi,
        hoa: monthlyHoa
      }
    ],
    cashToCloseBeforeCredit,
    earnestMoneyDeposit,
    cashToClose: cashToClose_1_0,
    extraCashNeeded: extraCash_1_0,
    breakEvenMonths: breakEven_1_0,
    totalSavings: totalSavings_1_0
  });

  // 2/1 Buydown Scenario
  const year1PI_2_1 = calculateMonthlyPI(loanAmount, configuration.twoOneYear1Rate);
  const year2PI_2_1 = calculateMonthlyPI(loanAmount, configuration.twoOneYear2Rate);
  const year3PI_2_1 = calculateMonthlyPI(loanAmount, configuration.twoOneYear3PlusRate);
  
  const year1Payment_2_1 = year1PI_2_1 + monthlyTaxes + monthlyInsurance + monthlyHoa + monthlyPmi;
  const year2Payment_2_1 = year2PI_2_1 + monthlyTaxes + monthlyInsurance + monthlyHoa + monthlyPmi;
  const year3Payment_2_1 = year3PI_2_1 + monthlyTaxes + monthlyInsurance + monthlyHoa + monthlyPmi;
  
  const builderCredit_2_1 = purchasePrice * (configuration.twoOneBuilderCreditPercent / 100);
  const cashToClose_2_1 = Math.max(0, cashToCloseBeforeCredit - builderCredit_2_1 - earnestMoneyDeposit);
  const extraCash_2_1 = cashToClose_2_1 - fixedCashToClose;
  
  const year1Savings = (baseMonthlyPayment - year1Payment_2_1) * 12;
  const year2Savings = (baseMonthlyPayment - year2Payment_2_1) * 12;
  const totalSavings_2_1 = year1Savings + year2Savings;
  
  const monthlySavingsYear1 = baseMonthlyPayment - year1Payment_2_1;
  const breakEven_2_1 = extraCash_2_1 > 0 ? Math.ceil(extraCash_2_1 / monthlySavingsYear1) : 0;

  scenarios.push({
    type: '2/1',
    name: '2/1 Buydown',
    description: 'Best value: Year 1 at -2%, Year 2 at -1%, then normal rate',
    points: configuration.twoOnePoints,
    pointsCost: configuration.twoOnePoints * onePoint,
    builderCreditPercent: configuration.twoOneBuilderCreditPercent,
    builderCredit: builderCredit_2_1,
    paymentSchedule: [
      {
        period: 'Year 1',
        rate: configuration.twoOneYear1Rate,
        monthlyPayment: year1Payment_2_1,
        principalAndInterest: year1PI_2_1,
        propertyTaxes: monthlyTaxes,
        homeInsurance: monthlyInsurance,
        pmi: monthlyPmi,
        hoa: monthlyHoa
      },
      {
        period: 'Year 2',
        rate: configuration.twoOneYear2Rate,
        monthlyPayment: year2Payment_2_1,
        principalAndInterest: year2PI_2_1,
        propertyTaxes: monthlyTaxes,
        homeInsurance: monthlyInsurance,
        pmi: monthlyPmi,
        hoa: monthlyHoa
      },
      {
        period: 'Years 3-30',
        rate: configuration.twoOneYear3PlusRate,
        monthlyPayment: year3Payment_2_1,
        principalAndInterest: year3PI_2_1,
        propertyTaxes: monthlyTaxes,
        homeInsurance: monthlyInsurance,
        pmi: monthlyPmi,
        hoa: monthlyHoa
      }
    ],
    cashToCloseBeforeCredit,
    earnestMoneyDeposit,
    cashToClose: cashToClose_2_1,
    extraCashNeeded: extraCash_2_1,
    breakEvenMonths: breakEven_2_1,
    totalSavings: totalSavings_2_1
  });

  // 3/2/1 Buydown Scenario
  const year1PI_3_2_1 = calculateMonthlyPI(loanAmount, configuration.threeTwoOneYear1Rate);
  const year2PI_3_2_1 = calculateMonthlyPI(loanAmount, configuration.threeTwoOneYear2Rate);
  const year3PI_3_2_1 = calculateMonthlyPI(loanAmount, configuration.threeTwoOneYear3Rate);
  const year4PI_3_2_1 = calculateMonthlyPI(loanAmount, configuration.threeTwoOneYear4PlusRate);
  
  const year1Payment_3_2_1 = year1PI_3_2_1 + monthlyTaxes + monthlyInsurance + monthlyHoa + monthlyPmi;
  const year2Payment_3_2_1 = year2PI_3_2_1 + monthlyTaxes + monthlyInsurance + monthlyHoa + monthlyPmi;
  const year3Payment_3_2_1 = year3PI_3_2_1 + monthlyTaxes + monthlyInsurance + monthlyHoa + monthlyPmi;
  const year4Payment_3_2_1 = year4PI_3_2_1 + monthlyTaxes + monthlyInsurance + monthlyHoa + monthlyPmi;
  
  const builderCredit_3_2_1 = purchasePrice * (configuration.threeTwoOneBuilderCreditPercent / 100);
  const cashToClose_3_2_1 = Math.max(0, cashToCloseBeforeCredit - builderCredit_3_2_1 - earnestMoneyDeposit);
  const extraCash_3_2_1 = cashToClose_3_2_1 - fixedCashToClose;
  
  const year1Savings_3 = (baseMonthlyPayment - year1Payment_3_2_1) * 12;
  const year2Savings_3 = (baseMonthlyPayment - year2Payment_3_2_1) * 12;
  const year3Savings_3 = (baseMonthlyPayment - year3Payment_3_2_1) * 12;
  const totalSavings_3_2_1 = year1Savings_3 + year2Savings_3 + year3Savings_3;
  
  const monthlySavingsYear1_3 = baseMonthlyPayment - year1Payment_3_2_1;
  const breakEven_3_2_1 = extraCash_3_2_1 > 0 ? Math.ceil(extraCash_3_2_1 / monthlySavingsYear1_3) : 0;

  scenarios.push({
    type: '3/2/1',
    name: '3/2/1 Buydown',
    description: 'Maximum savings: Year 1 at -3%, Year 2 at -2%, Year 3 at -1%, then normal',
    points: configuration.threeTwoOnePoints,
    pointsCost: configuration.threeTwoOnePoints * onePoint,
    builderCreditPercent: configuration.threeTwoOneBuilderCreditPercent,
    builderCredit: builderCredit_3_2_1,
    paymentSchedule: [
      {
        period: 'Year 1',
        rate: configuration.threeTwoOneYear1Rate,
        monthlyPayment: year1Payment_3_2_1,
        principalAndInterest: year1PI_3_2_1,
        propertyTaxes: monthlyTaxes,
        homeInsurance: monthlyInsurance,
        pmi: monthlyPmi,
        hoa: monthlyHoa
      },
      {
        period: 'Year 2',
        rate: configuration.threeTwoOneYear2Rate,
        monthlyPayment: year2Payment_3_2_1,
        principalAndInterest: year2PI_3_2_1,
        propertyTaxes: monthlyTaxes,
        homeInsurance: monthlyInsurance,
        pmi: monthlyPmi,
        hoa: monthlyHoa
      },
      {
        period: 'Year 3',
        rate: configuration.threeTwoOneYear3Rate,
        monthlyPayment: year3Payment_3_2_1,
        principalAndInterest: year3PI_3_2_1,
        propertyTaxes: monthlyTaxes,
        homeInsurance: monthlyInsurance,
        pmi: monthlyPmi,
        hoa: monthlyHoa
      },
      {
        period: 'Years 4-30',
        rate: configuration.threeTwoOneYear4PlusRate,
        monthlyPayment: year4Payment_3_2_1,
        principalAndInterest: year4PI_3_2_1,
        propertyTaxes: monthlyTaxes,
        homeInsurance: monthlyInsurance,
        pmi: monthlyPmi,
        hoa: monthlyHoa
      }
    ],
    cashToCloseBeforeCredit,
    earnestMoneyDeposit,
    cashToClose: cashToClose_3_2_1,
    extraCashNeeded: extraCash_3_2_1,
    breakEvenMonths: breakEven_3_2_1,
    totalSavings: totalSavings_3_2_1
  });

  return scenarios;
};
